"use client";

import React, { useEffect, useRef, useState } from "react";
import { Trash2, Paperclip } from "lucide-react";

import { Button } from "@/components/atoms/button";
import { ESubmitStatus } from "@/types/common";

// Use Set for allowedTypes for efficient lookup
const allowedTypes = new Set([
  "application/pdf",
  "application/msword", // .doc
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.oasis.opendocument.text",
  "text/csv",
]);
interface IUploadAttachmentProps {
  onChange: (files: File[]) => void;
  label?: string;
  submitStatus?: ESubmitStatus;
}
/**
 * UploadAttachment Component
 * Handles file upload functionality with validation for allowed file types and upload limits.
 * Supports max 3 files and clears uploaded files on form submission success or failure.
 */
export default function UploadAttachment({
  onChange,
  label,
  submitStatus,
}: Readonly<IUploadAttachmentProps>) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);

  /** Handles file selection and validates file type and count */
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []);

    const valid = selected.filter((file) => allowedTypes.has(file.type));

    if (valid?.length !== selected?.length) {
      setError(
        "無効なファイル形式です。許可されている形式: PDF、Word、PPT、Excel、ODT、CSV",
      );
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    if (files?.length + valid?.length > 3) {
      setError("最大3つのファイルをアップロードできます。");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    setFiles((prev) => [...prev, ...valid]);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDelete = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  /** Updates parent form whenever file list changes */
  useEffect(() => {
    if (files?.length > 0) {
      onChange(files);
    } else {
      onChange([]);
    }
  }, [files, onChange]);

  /** Resets uploaded files after form submission (success or error) */
  useEffect(() => {
    if (
      submitStatus === ESubmitStatus.SUCCESS ||
      submitStatus === ESubmitStatus.ERROR
    ) {
      Promise.resolve().then(() => {
        setFiles([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      });
    }
  }, [submitStatus]);

  return (
    <div className="w-full">
      {files?.length < 3 && (
        <div className="mb-2 flex items-center justify-end gap-2">
          <Button
            type="button"
            hierarchy="ghost"
            onClick={() => fileInputRef.current?.click()}
          >
            <Paperclip size={16} className="mr-1" />
            {label || "Attach file"}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleFileSelect}
            accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.odt,.csv"
          />
        </div>
      )}

      <ul className="space-y-1 text-sm text-[var(--text-base-primary)]">
        {files.map((file, index) => {
          const uploadedFilesKey = `uploaded-files-${index}`;
          return (
            <li
              key={uploadedFilesKey}
              className="flex items-center justify-between"
            >
              {file.name}
              <Button
                type="button"
                hierarchy="ghost"
                onClick={() => handleDelete(index)}
                className="text-[var(--text-gray-normal)] hover:text-red-600"
              >
                <Trash2 size={16} />
              </Button>
            </li>
          );
        })}
      </ul>

      {error && (
        <p className="mt-2 text-sm font-medium text-red-600">{error}</p>
      )}
    </div>
  );
}
